
// Import React and useState for form state management
import React, { useState } from "react";
// Import Hero component for the page banner
import Hero from "./components/Hero";
// Import image for the hero section
import HeroImage from "../assets/images/businesswoman-reading-message-mobile-phone.jpg";
// Import CSS for contact page styling
import "../css/ContactUs.css";
// Import useNavigate for navigation
import { useNavigate } from "react-router-dom";



// ContactUsPage component displays a contact form and related info
function ContactUsPage() {
  // State for form fields
  const [form, setForm] = useState({
    name: "",
    number: "",
    email: "",
    message: "",
    consent: false,
  });

  // State for form validation errors
  const [errors, setErrors] = useState({});
  // Hook for navigation
  const navigate = useNavigate();

  // Handle input changes for all fields
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    // Clear field error as user types
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // Validate form fields before submit
  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Name is required";
    if (!form.number.trim()) next.number = "Number is required";
    if (!form.email.trim()) next.email = "Email is required";
    if (!form.message.trim()) next.message = "Message is required";
    if (!form.consent)
      next.consent = "Please confirm you have read the privacy policy";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!validate()) return;
      const to = process.env.REACT_APP_ADVISER_EMAIL;
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        number: form.number.trim(),
        message: form.message.trim(),
        to: (to || "").trim(),
      };
      const url = `${process.env.REACT_APP_SERVER_URL}/send-adviser-contact-confirmation`;
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const errText = await res.text();
        console.log("API error:", errText);
        return;
      }
      // Reset form and errors on success
      setForm({
        name: "",
        number: "",
        email: "",
        message: "",
        consent: false,
      });
      setErrors({});
      console.log("Sent successfully");
    } catch (err) {
      console.log("handleSubmit error:", err);
    }
  };


  return (
    <>
      {/* Hero section with background image and title */}
      <Hero title={"Contact us"} subtitle={""} backgroundImage={HeroImage} />

      {/* Contact form section */}
      <section className="contact-form-wrap">
        <form className="contact-form" onSubmit={handleSubmit}>
          <div className="contact-row">
            {/* Name field */}
            <div className="contact-field">
              <label>Name:</label>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                className={errors.name ? "has-error" : ""}
              />
              {errors.name ? (
                <span className="error-text">{errors.name}</span>
              ) : null}
            </div>

            {/* Number field */}
            <div className="contact-field">
              <label>Number:</label>
              <input
                name="number"
                value={form.number}
                onChange={handleChange}
                className={errors.number ? "has-error" : ""}
              />
              {errors.number ? (
                <span className="error-text">{errors.number}</span>
              ) : null}
            </div>
          </div>

          {/* Email field */}
          <div className="contact-field">
            <label>Email:</label>
            <input
              name="email"
              value={form.email}
              onChange={handleChange}
              className={errors.email ? "has-error" : ""}
            />
            {errors.email ? (
              <span className="error-text">{errors.email}</span>
            ) : null}
          </div>

          {/* Message field */}
          <div className="contact-field">
            <label>Message:</label>
            <textarea
              name="message"
              value={form.message}
              onChange={handleChange}
              rows={8}
              className={errors.message ? "has-error" : ""}
            />
            {errors.message ? (
              <span className="error-text">{errors.message}</span>
            ) : null}
          </div>

          {/* Consent checkbox */}
          <div className="contact-consent">
            <input
              type="checkbox"
              name="consent"
              checked={form.consent}
              onChange={handleChange}
              id="consent"
            />
            <label htmlFor="consent">
              *I confirm I have read the{" "}
              <span
                style={{ textDecoration: "underline", cursor: "pointer" }}
                onClick={() => navigate("/privacy-policy")}
              >
                privacy policy
              </span>
            </label>
          </div>

          {/* Submit button */}
          <button className="contact-submit" type="submit">
            Submit
          </button>
        </form>
        {/* Consent error message */}
        {errors.consent ? (
          <span className="error-text">{errors.consent}</span>
        ) : null}
      </section>

      {/* Privacy notice section */}
      <section className="services-section">
        <p>
          Before you supply any personal details to us via the contact form,
          please ensure you have read our{" "}
          <span
            onClick={() => navigate("/privacy-policy")}
            style={{ textDecoration: "underline", cursor: "pointer" }}
          >
            privacy notice
          </span>
          . This notice sets out how we will process your personal data in line
          with General Data Protection Regulations. Once you have read the{" "}
          <span
            onClick={() => navigate("/privacy-policy")}
            style={{ textDecoration: "underline", cursor: "pointer" }}
          >
             privacy notice
          </span>
          , please tick to confirm that you have read it and that you agree to
          Portfolio Finance processing your personal information for the purpose
          of contacting you. We will not use the details you provide us in the
          contact form for marketing purposes.
        </p>
      </section>

      {/* Complaints procedure section */}
      <h2 className="ContactUs">Complaints Procedure</h2>
      <div className="divider"></div>
      <p className="services-description" style={{ color: "#000" }}>
        If you wish to register a complaint, please write to Complaints Officer,
        Rosemount Financial Solutions IFA Ltd, Rosemount House, 2-4 Chequers
        Road, Basingstoke, RG21 7PU.  Telephone{" "}
        <a href="tel:01256405460">01256 405460</a> or email 
        <a href="mailto:complaints@rosemountfsIFA.co.uk">
          complaints@rosemountfsIFA.co.uk
        </a>
         
        <br /> <div style={{ height: 30 }}></div>  A summary of our internal
        complaints handling procedures for the reasonable and prompt handling of
        complaints is available on request and if you cannot settle your
        complaint with us, you may be entitled to refer it to the Financial
        Ombudsman Service at www.financial-ombudsman.org.uk  or by contacting
        them on 0800 023 4567.
      </p>
    </>
  );
}

// Export the ContactUsPage component as default
export default ContactUsPage;
